import { useCallback, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'
import { rem } from 'polished'
import { trackEvent } from 'analytics/analytics'
import { promotionClickEvent, promotionViewEvent } from 'analytics/snowplow/events'
import Image from 'components/Common/Image'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import NeutralLabel from 'LuxKit/Label/NeutralLabel'
import HighlightLabel from 'LuxKit/Label/HighlightLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import TextButton from 'LuxKit/Button/TextButton'
import noop from 'lib/function/noop'
import { RED_CENTRE_SEARCH_TILE_URL } from 'sponsoredSearch/redCentreSearchTile'
import _SearchTileBase, { _searchTileContainerQueryUp } from '../_SearchTileBase'
import _SponsoredTileContent from './_SponsoredTileContent'
import { SEARCH_TILE_BREAKPOINTS } from '../searchTilePricingLayout'

// Campaign deadline: 30 November 2026 at 11:59 pm Australia/Sydney (AEDT).
const CAMPAIGN_END_DATE = '2026-11-30T23:59:00+11:00'

const HERO_IMAGE: App.Image = { id: 'search-tile-ads-nt-red-centre-hero' }
const LOGO_IMAGE: App.Image = { id: 'search-tile-ads-nt-red-centre-logo-light' }
const PROMOTION_CONTEXT = { promotionType: 'banner' as const, promotionName: 'sponsored-search-tile-red-centre' }

const TABLET_ROW_QUERY = `@container search-tile (${rem(SEARCH_TILE_BREAKPOINTS.desktopSmall)} <= width < ${rem(SEARCH_TILE_BREAKPOINTS.desktop)})`

const Hero = styled.div`
  grid-area: hero;
  position: relative;
  align-self: start;

  ${TABLET_ROW_QUERY} {
    align-self: end;
  }
`

const HeroImage = styled(Image)`
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 568 / 319;
`

const PromotionLabels = styled(LabelGroup)`
  position: absolute;
  inset-block-start: ${rem(12)};
  inset-inline: ${rem(12)};
`

const PartnerPromotionBadge = styled(NeutralLabel)`
  position: absolute;
  inset-inline-end: ${rem(12)};
  inset-block-end: ${rem(10)};
  color: ${p => p.theme.palette.highlight.primary.darkForeground};
`

const Content = styled(_SponsoredTileContent)`
  ${TABLET_ROW_QUERY} {
    padding-block: ${rem(12)} ${rem(20)};
    gap: ${rem(12)};
  }
`

const Title = styled(Heading)`
  ${TABLET_ROW_QUERY} {
    grid-area: title;
  }
`

const Description = styled(BodyText)`
  ${TABLET_ROW_QUERY} {
    grid-area: description;
  }
`

const Primary = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${TABLET_ROW_QUERY} {
    display: grid;
    grid-template:
      "title logo" auto
      "description description" auto / minmax(0, 1fr) ${rem(112)};
    gap: ${rem(4)} ${rem(12)};
    align-items: start;
  }

  ${_searchTileContainerQueryUp.desktop} {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
`

const Copy = styled.div`
  min-width: 0;
  order: 1;

  ${TABLET_ROW_QUERY} {
    display: contents;
  }

  ${_searchTileContainerQueryUp.desktop} {
    order: 0;
  }
`

// Use the light-background artwork so the logo sits directly on the white tile.
const Logo = styled(Image)`
  display: block;
  width: ${rem(160)};
  height: auto;
  align-self: flex-end;
  flex-shrink: 0;

  ${TABLET_ROW_QUERY} {
    grid-area: logo;
    width: ${rem(112)};
    align-self: start;
  }

  ${_searchTileContainerQueryUp.desktop} {
    align-self: flex-start;
  }
`

const CtaButton = styled(TextButton)`
  --button-background: ${p => p.theme.palette.neutral.surface.darkBackground};
  --button-contrast: ${p => p.theme.palette.neutral.surface.darkContrast};
  min-width: ${rem(200)};
  height: ${rem(40)};
`

interface Props {
  onView?: () => void
}

function RedCentreSponsoredSearchTile({ onView = noop }: Props) {
  const intl = useIntl()
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const handleImageLoaded = useCallback(() => {
    dispatchOfferListEvent({ type: OfferListEvents.offerReady })
  }, [dispatchOfferListEvent])
  const hasFiredView = useRef(false)
  const [inViewRef, inView] = useInView({ triggerOnce: true, threshold: 0.5 })

  useEffect(() => {
    if (inView && !hasFiredView.current) {
      hasFiredView.current = true
      trackEvent(promotionViewEvent({ promotionContext: PROMOTION_CONTEXT }))
      onView()
    }
  }, [inView, onView])

  const handleClick = useCallback(() => {
    trackEvent(promotionClickEvent({ promotionContext: PROMOTION_CONTEXT }))
  }, [])

  const heroAlt = intl.formatMessage({
    id: '6tm266AUj9ysteVm_kK_',
    defaultMessage: 'Discover the Red Centre',
    description: 'Alternative text for the Red Centre campaign image in hotel search results',
  })

  const logoAlt = intl.formatMessage({
    id: 'ExQprFClVdlUZ_M2WlX8',
    defaultMessage: 'Red Centre, Northern Territory',
    description: 'Alternative text for the Tourism NT partner logo on the Red Centre sponsored search tile',
  })

  return <_SearchTileBase ref={inViewRef} fitContent naturalHeroHeight data-testid="red-centre-sponsored-search-tile">
    <Hero>
      <HeroImage
        image={HERO_IMAGE}
        width={568}
        loading="lazy"
        onLoad={handleImageLoaded}
        alt={heroAlt}
      />
      <PromotionLabels>
        <HighlightLabel>{intl.formatMessage({
          id: 'wplMaYRri8Yp2HEx4aRd',
          defaultMessage: 'Limited time offer',
          description: 'Green promotional label at the top of the Red Centre sponsored search tile image',
        })}
        </HighlightLabel>
        <OfferUrgencyCountdownLabel
          endDate={CAMPAIGN_END_DATE}
          prefixLabel={intl.formatMessage({
            id: 'h6C2gWdKHUq5atOZyF0E',
            defaultMessage: 'Offer',
            description: 'Prefix for the expiry countdown on the Red Centre sponsored search tile image',
          })}
          tagPlace="offer-search-tile"
          overrideShowWithinXDays={Infinity}
        />
      </PromotionLabels>
      <PartnerPromotionBadge>{intl.formatMessage({
        id: 'u4IW5Efz4msCkhNSIZ3-',
        defaultMessage: 'Partner promotion',
        description: 'Disclosure label in the bottom-right of the Red Centre campaign image identifying a partner promotion',
      })}
      </PartnerPromotionBadge>
    </Hero>
    <Content
      primary={<Primary>
        <Copy>
          <Title variant="heading5">{intl.formatMessage({
            id: 'Rc0H4OP7VOs5c2T3TZtN',
            defaultMessage: 'Discover the Red Centre',
            description: 'Heading of the Red Centre sponsored campaign tile in hotel search results',
          })}
          </Title>
          <Description variant="medium">{intl.formatMessage({
            id: 'HwqiHHm9_JheCVHL0lax',
            defaultMessage: '6 nights in three iconic stays across Alice Springs, Kings Canyon & Uluru from $2,399 including car hire',
            description: 'Body copy of the Red Centre sponsored campaign tile in Australian hotel search results',
          })}
          </Description>
        </Copy>
        <Logo
          image={LOGO_IMAGE}
          width={160}
          loading="lazy"
          alt={logoAlt}
        />
      </Primary>}
      action={<CtaButton kind="primary" href={RED_CENTRE_SEARCH_TILE_URL} target="_blank" rel="noopener noreferrer" onClick={handleClick}>
        {intl.formatMessage({
          id: 'v4xzX7I3tlzAU4TW5dyA',
          defaultMessage: 'Book now',
          description: 'Call to action on the Red Centre sponsored search tile linking to the campaign landing page',
        })}
      </CtaButton>}
    />
  </_SearchTileBase>
}

export default RedCentreSponsoredSearchTile
